import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faExclamationTriangle,
  faCheck,
  faTimes,
  faEye,
  faReply,
  faSortUp,
  faSortDown,
  faSort,
  faCalendar,
  faUser,
  faBug,
  faPlay,
  faVolumeUp,
  faClosedCaptioning,
  faLink
} from '@fortawesome/free-solid-svg-icons';
import '../styles/AdminReports.css';

const AdminReports = () => {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // Mock reports data
  const mockReports = [
    {
      id: 1,
      movie_name: 'One Piece',
      movie_id: 1,
      episode: 1000,
      server: 'VIP',
      report_type: 'video_error',
      title: 'Video không tải được',
      description: 'Video bị lỗi ở phút thứ 15, màn hình đen hoàn toàn',
      reporter_name: 'Nguyễn Văn A',
      reporter_email: 'nguyenvana@email.com',
      reporter_ip: '192.168.1.100',
      status: 'pending',
      priority: 'high',
      admin_response: null,
      resolved_by: null,
      resolved_at: null,
      created_at: '2024-01-20T10:30:00Z',
      updated_at: '2024-01-20T10:30:00Z'
    },
    {
      id: 2,
      movie_name: 'Attack on Titan',
      movie_id: 2,
      episode: 75,
      server: 'HD',
      report_type: 'audio_error',
      title: 'Không có tiếng',
      description: 'Video phát bình thường nhưng không có âm thanh từ đầu đến cuối tập',
      reporter_name: 'Trần Thị B',
      reporter_email: 'tranthib@email.com',
      reporter_ip: '192.168.1.101',
      status: 'resolved',
      priority: 'medium',
      admin_response: 'Đã khắc phục lỗi âm thanh, cảm ơn bạn đã báo cáo.',
      resolved_by: 'Admin',
      resolved_at: '2024-01-19T14:20:00Z',
      created_at: '2024-01-19T09:15:00Z',
      updated_at: '2024-01-19T14:20:00Z'
    },
    {
      id: 3,
      movie_name: 'Demon Slayer',
      movie_id: 3,
      episode: 15,
      server: 'FB',
      report_type: 'subtitle_error',
      title: 'Sub không khớp với video',
      description: 'Phụ đề bị lệch khoảng 5 giây so với video, rất khó theo dõi',
      reporter_name: 'Lê Văn C',
      reporter_email: 'levanc@email.com',
      reporter_ip: '192.168.1.102',
      status: 'in_progress',
      priority: 'medium',
      admin_response: 'Đang kiểm tra và điều chỉnh phụ đề...',
      resolved_by: null,
      resolved_at: null,
      created_at: '2024-01-18T16:45:00Z',
      updated_at: '2024-01-19T08:30:00Z'
    },
    {
      id: 4,
      movie_name: 'Jujutsu Kaisen',
      movie_id: 5,
      episode: 24,
      server: 'VIP',
      report_type: 'link_broken',
      title: 'Link phim bị hỏng',
      description: 'Khi click vào tập 24 thì hiện lỗi 404 not found',
      reporter_name: 'Phạm Thị D',
      reporter_email: 'phamthid@email.com',
      reporter_ip: '192.168.1.103',
      status: 'rejected',
      priority: 'low',
      admin_response: 'Link đã hoạt động bình thường, có thể do lỗi tạm thời.',
      resolved_by: 'Admin',
      resolved_at: '2024-01-17T11:00:00Z',
      created_at: '2024-01-17T08:20:00Z',
      updated_at: '2024-01-17T11:00:00Z'
    },
    {
      id: 5,
      movie_name: 'Naruto',
      movie_id: 6,
      episode: 500,
      server: 'HD',
      report_type: 'other',
      title: 'Chất lượng video kém',
      description: 'Video bị mờ và pixelated, không thể xem được',
      reporter_name: 'Hoàng Văn E',
      reporter_email: 'hoangvane@email.com',
      reporter_ip: '192.168.1.104',
      status: 'pending',
      priority: 'low',
      admin_response: null,
      resolved_by: null,
      resolved_at: null,
      created_at: '2024-01-16T13:10:00Z',
      updated_at: '2024-01-16T13:10:00Z'
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setReports(mockReports);
      setFilteredReports(mockReports);
      setLoading(false);
    }, 1000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterAndSortReports();
  }, [searchTerm, sortField, sortDirection, filterStatus, filterType, reports]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterAndSortReports = () => {
    let filtered = [...reports];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(report =>
        report.movie_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.reporter_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(report => report.status === filterStatus);
    }

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(report => report.report_type === filterType);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredReports(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleStatusChange = (reportId, newStatus, response = null) => {
    setReports(reports.map(report => {
      if (report.id === reportId) {
        return {
          ...report,
          status: newStatus,
          admin_response: response || report.admin_response,
          resolved_by: newStatus === 'resolved' ? 'Admin' : null,
          resolved_at: newStatus === 'resolved' ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        };
      }
      return report;
    }));
  };

  const handleResponse = (report) => {
    setSelectedReport(report);
    setShowResponseModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'in_progress': return '#3b82f6';
      case 'resolved': return '#10b981';
      case 'rejected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Chờ xử lý';
      case 'in_progress': return 'Đang xử lý';
      case 'resolved': return 'Đã giải quyết';
      case 'rejected': return 'Từ chối';
      default: return 'Không xác định';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'high': return 'Cao';
      case 'medium': return 'Trung bình';
      case 'low': return 'Thấp';
      default: return 'Không xác định';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video_error': return faPlay;
      case 'audio_error': return faVolumeUp;
      case 'subtitle_error': return faClosedCaptioning;
      case 'link_broken': return faLink;
      default: return faBug;
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'video_error': return 'Lỗi video';
      case 'audio_error': return 'Lỗi âm thanh';
      case 'subtitle_error': return 'Lỗi phụ đề';
      case 'link_broken': return 'Link hỏng';
      case 'other': return 'Khác';
      default: return 'Không xác định';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  if (loading) {
    return (
      <div className="admin-reports">
        <div className="reports-loading">
          <div className="loading-spinner"></div>
          <p>Đang tải danh sách báo cáo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-reports">
      <div className="reports-header">
        <div className="header-left">
          <h1>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Báo cáo Lỗi
          </h1>
          <p>Quản lý các báo cáo lỗi phim từ người dùng</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="reports-stats">
        <div className="stat-card">
          <div className="stat-icon pending">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{reports.filter(r => r.status === 'pending').length}</div>
            <div className="stat-label">Chờ xử lý</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon progress">
            <FontAwesomeIcon icon={faBug} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{reports.filter(r => r.status === 'in_progress').length}</div>
            <div className="stat-label">Đang xử lý</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon success">
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{reports.filter(r => r.status === 'resolved').length}</div>
            <div className="stat-label">Đã giải quyết</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon danger">
            <FontAwesomeIcon icon={faTimes} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{reports.filter(r => r.priority === 'high').length}</div>
            <div className="stat-label">Ưu tiên cao</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="reports-filters">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Tìm kiếm báo cáo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="pending">Chờ xử lý</option>
            <option value="in_progress">Đang xử lý</option>
            <option value="resolved">Đã giải quyết</option>
            <option value="rejected">Từ chối</option>
          </select>
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">Tất cả loại lỗi</option>
            <option value="video_error">Lỗi video</option>
            <option value="audio_error">Lỗi âm thanh</option>
            <option value="subtitle_error">Lỗi phụ đề</option>
            <option value="link_broken">Link hỏng</option>
            <option value="other">Khác</option>
          </select>
        </div>
      </div>

      {/* Reports Table */}
      <div className="reports-table-container">
        <table className="reports-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('report_type')} className="sortable">
                Loại lỗi
                <FontAwesomeIcon 
                  icon={sortField === 'report_type' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('movie_name')} className="sortable">
                Phim
                <FontAwesomeIcon 
                  icon={sortField === 'movie_name' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th>Mô tả</th>
              <th onClick={() => handleSort('reporter_name')} className="sortable">
                Người báo cáo
                <FontAwesomeIcon 
                  icon={sortField === 'reporter_name' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('priority')} className="sortable">
                Ưu tiên
                <FontAwesomeIcon 
                  icon={sortField === 'priority' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('status')} className="sortable">
                Trạng thái
                <FontAwesomeIcon 
                  icon={sortField === 'status' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('created_at')} className="sortable">
                Ngày báo cáo
                <FontAwesomeIcon 
                  icon={sortField === 'created_at' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports.map(report => (
              <tr key={report.id} className={`priority-${report.priority}`}>
                <td>
                  <div className="report-type">
                    <FontAwesomeIcon icon={getTypeIcon(report.report_type)} />
                    <span>{getTypeText(report.report_type)}</span>
                  </div>
                </td>
                <td>
                  <div className="movie-info">
                    <div className="movie-name">{report.movie_name}</div>
                    <div className="episode-info">
                      Tập {report.episode} - Server {report.server}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="report-description">
                    <div className="report-title">{report.title}</div>
                    <div className="report-content">
                      {report.description.length > 80 
                        ? report.description.substring(0, 80) + '...'
                        : report.description
                      }
                    </div>
                  </div>
                </td>
                <td>
                  <div className="reporter-info">
                    <div className="reporter-name">
                      <FontAwesomeIcon icon={faUser} />
                      {report.reporter_name}
                    </div>
                    <div className="reporter-email">{report.reporter_email}</div>
                  </div>
                </td>
                <td>
                  <div 
                    className="priority-badge"
                    style={{ backgroundColor: getPriorityColor(report.priority) }}
                  >
                    {getPriorityText(report.priority)}
                  </div>
                </td>
                <td>
                  <div 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(report.status) }}
                  >
                    {getStatusText(report.status)}
                  </div>
                </td>
                <td>
                  <div className="report-date">
                    <FontAwesomeIcon icon={faCalendar} />
                    {formatDate(report.created_at)}
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="action-btn view"
                      title="Xem chi tiết"
                      onClick={() => handleResponse(report)}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    <button 
                      className="action-btn reply"
                      title="Phản hồi"
                      onClick={() => handleResponse(report)}
                    >
                      <FontAwesomeIcon icon={faReply} />
                    </button>
                    <button 
                      className="action-btn resolve"
                      title="Đánh dấu đã giải quyết"
                      onClick={() => handleStatusChange(report.id, 'resolved')}
                      disabled={report.status === 'resolved'}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button 
                      className="action-btn reject"
                      title="Từ chối"
                      onClick={() => handleStatusChange(report.id, 'rejected')}
                      disabled={report.status === 'rejected'}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Response Modal */}
      {showResponseModal && selectedReport && (
        <ResponseModal
          report={selectedReport}
          onSave={(response) => {
            handleStatusChange(selectedReport.id, 'in_progress', response);
            setShowResponseModal(false);
            setSelectedReport(null);
          }}
          onCancel={() => {
            setShowResponseModal(false);
            setSelectedReport(null);
          }}
        />
      )}
    </div>
  );
};

// Response Modal Component
const ResponseModal = ({ report, onSave, onCancel }) => {
  const [response, setResponse] = useState(report.admin_response || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(response);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content response-modal">
        <div className="modal-header">
          <h3>
            <FontAwesomeIcon icon={faReply} />
            Phản hồi báo cáo
          </h3>
          <button className="modal-close" onClick={onCancel}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="report-details">
          <div className="detail-row">
            <strong>Phim:</strong> {report.movie_name} - Tập {report.episode}
          </div>
          <div className="detail-row">
            <strong>Loại lỗi:</strong> {report.report_type}
          </div>
          <div className="detail-row">
            <strong>Tiêu đề:</strong> {report.title}
          </div>
          <div className="detail-row">
            <strong>Mô tả:</strong> {report.description}
          </div>
          <div className="detail-row">
            <strong>Người báo cáo:</strong> {report.reporter_name} ({report.reporter_email})
          </div>
        </div>

        <form onSubmit={handleSubmit} className="response-form">
          <div className="form-group">
            <label>Phản hồi của admin:</label>
            <textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Nhập phản hồi cho người dùng..."
              rows={5}
              required
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>
              <FontAwesomeIcon icon={faTimes} />
              Hủy
            </button>
            <button type="submit" className="btn-save">
              <FontAwesomeIcon icon={faReply} />
              Gửi phản hồi
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminReports;